import { THREE } from "../config/ThreeConfig.js";
import { isTestEnvironment } from "../utils/runtimeEnv.js";
/**
 * Scene Manager
 * Handles Three.js scene, camera, lighting, and skybox
 */


export class Scene {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.lighting = {
      ambient: null,
      directional: null,
      pointLights: [],
    };
    this.skybox = null;
    this.stars = null;
    this.starsFar = null;
    this.dust = null;
    this._dustVel = null;
    this.nebulaLayers = [];
    this._nebulaTextures = [];
    this.setPieces = [];
  }
  
  async init() {
    // Create scene
    this.scene = new THREE.Scene();
    // Slightly darker base for contrast (retro space still reads via emissive + rim light).
    this.scene.background = new THREE.Color(0x06061a);
    this.scene.fog = new THREE.FogExp2(0x06061a, 0.0008);
    
    // Create camera
  const aspect = Math.max(0.1, window.innerWidth / window.innerHeight);
  const baseFOV = 60;
  let initFOV = baseFOV;
  if (aspect < 1.2) {
    const refAspect = 16 / 9;
    const vFovRad = baseFOV * (Math.PI / 180);
    const hFovRad = 2 * Math.atan(Math.tan(vFovRad / 2) * refAspect);
    const adaptiveVFovRad = 2 * Math.atan(Math.tan(hFovRad / 2) / aspect);
    initFOV = Math.min(95, Math.max(60, (adaptiveVFovRad * 180 / Math.PI)));
  }

this.camera = new THREE.PerspectiveCamera(
      initFOV,
      aspect,
      0.5,
      6500
    );
    this.camera.position.set(0, 35, 140);
    
    // Setup lighting
    this.setupLighting();
    
    // Create space environment
    this.createSkybox();
    this.createStars();
    this.createDust();
    this.createNebula();
    
    return Promise.resolve();
  }
  
  setupLighting() {
    // Ambient light
    // Avoid flat ambient wash; preserve shape/shading.
    this.lighting.ambient = new THREE.AmbientLight(0x3a3a55, 0.62);
    this.scene.add(this.lighting.ambient);
    
    // Directional light (sun)
    this.lighting.directional = new THREE.DirectionalLight(0xffffff, 1.25);
    this.lighting.directional.position.set(500, 500, 500);
    // Shadows are disabled at the renderer for performance (space read is better via emissive/rim lights).
    this.lighting.directional.castShadow = false;
    this.scene.add(this.lighting.directional);
    
    // Rim light for dramatic effect
    const rimLight = new THREE.DirectionalLight(0x22a0ff, 0.95);
    rimLight.position.set(-200, 100, -200);
    this.scene.add(rimLight);

    // Camera "headlight" to prevent objects going dark at odd angles
    const headlight = new THREE.PointLight(0xffffff, 1.0, 1300, 2);
    headlight.position.set(0, 30, 100);
    this.camera.add(headlight);
    this.scene.add(this.camera);
  }
  
  createStars() {
    // Create star field
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 10000;
    const positions = new Float32Array(starCount * 3);
    const colors = new Float32Array(starCount * 3);
    
    for (let i = 0; i < starCount * 3; i += 3) {
      // Random position in sphere
      const radius = 2000 + Math.random() * 3000;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i + 2] = radius * Math.cos(phi);
      
      // Slight color variation
      const colorVariation = 0.8 + Math.random() * 0.2;
      colors[i] = colorVariation;
      colors[i + 1] = colorVariation;
      colors[i + 2] = colorVariation + Math.random() * 0.1;
    }
    
    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const starMaterial = new THREE.PointsMaterial({
      size: 1,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
    });
    
    this.stars = new THREE.Points(starGeometry, starMaterial);
    this.scene.add(this.stars);

    // Far field: fewer, slightly larger stars for parallax depth.
    const farGeometry = new THREE.BufferGeometry();
    const farCount = 2200;
    const fPos = new Float32Array(farCount * 3);
    const fCol = new Float32Array(farCount * 3);
    for (let i = 0; i < farCount; i++) {
      const i3 = i * 3;
      const radius = 3800 + Math.random() * 2200;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      fPos[i3] = radius * Math.sin(phi) * Math.cos(theta);
      fPos[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      fPos[i3 + 2] = radius * Math.cos(phi);
      const v = 0.65 + Math.random() * 0.35;
      fCol[i3] = v;
      fCol[i3 + 1] = v;
      fCol[i3 + 2] = Math.min(1, v + Math.random() * 0.25);
    }
    farGeometry.setAttribute('position', new THREE.BufferAttribute(fPos, 3));
    farGeometry.setAttribute('color', new THREE.BufferAttribute(fCol, 3));
    const farMat = new THREE.PointsMaterial({
      size: 1.6,
      vertexColors: true,
      transparent: true,
      opacity: 0.35,
      sizeAttenuation: true,
      depthWrite: false,
    });
    this.starsFar = new THREE.Points(farGeometry, farMat);
    this.scene.add(this.starsFar);
  }

  createDust() {
    // A small "space dust" field moving toward the camera adds motion/pizzaz on rails.
    const count = 850;
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 900;
      positions[i3 + 1] = (Math.random() - 0.5) * 520;
      positions[i3 + 2] = -2800 + Math.random() * 3200;
      velocities[i] = 120 + Math.random() * 220;
    }

    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this._dustVel = velocities;

    const mat = new THREE.PointsMaterial({
      size: 0.9,
      color: 0xffffff,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      sizeAttenuation: true,
    });

    this.dust = new THREE.Points(geom, mat);
    this.scene.add(this.dust);
  }

  createNebula() {
    // Uses existing UI background texture as an in-world parallax layer.
    if (isTestEnvironment()) return;
    if (typeof document === "undefined" || typeof Image === "undefined") return;

    const makeProceduralNebulaTexture = (palette) => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      // Background gradient.
      const g = ctx.createRadialGradient(256, 256, 0, 256, 256, 320);
      g.addColorStop(0, palette.inner);
      g.addColorStop(0.55, palette.mid);
      g.addColorStop(1, palette.outer);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 512, 512);

      // Soft noise blobs (cheap “nebula clouds”).
      for (let i = 0; i < 240; i++) {
        const x = Math.random() * 512;
        const y = Math.random() * 512;
        const r = 18 + Math.random() * 90;
        const a = 0.02 + Math.random() * 0.06;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `rgba(255,255,255,${a})`);
        grad.addColorStop(1, `rgba(255,255,255,0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Subtle star specks.
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      for (let i = 0; i < 900; i++) {
        const x = Math.random() * 512;
        const y = Math.random() * 512;
        const s = Math.random() < 0.92 ? 1 : 2;
        ctx.fillRect(x, y, s, s);
      }

      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      // Clamp and keep static: scrolling repeated nebula textures can expose a
      // vertical wrap seam during boost/FOV changes.
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.repeat.set(1, 1);
      tex.offset.set(0, 0);
      tex.magFilter = THREE.LinearFilter;
      tex.minFilter = THREE.LinearMipMapLinearFilter;
      tex.needsUpdate = true;
      return tex;
    };

    const loader = new THREE.TextureLoader();
    const urls = [
      '/ui/backgrounds/1024/nebula_n64_corridor.png',
      '/ui/backgrounds/2048/nebula_1.png',
      '/ui/backgrounds/1024/nebula_1.png',
    ];

    const tryLoad = (i = 0) => {
      const url = urls[i];
      if (!url) return;
      loader.load(
        url,
        (tex) => {
          if (!tex) return;
          tex.colorSpace = THREE.SRGBColorSpace;
          // Avoid repeated texture seams becoming visible as vertical lines
          // when boost widens the camera FOV.
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.repeat.set(1, 1);
          tex.offset.set(0, 0);
          tex.magFilter = THREE.LinearFilter;
          tex.minFilter = THREE.LinearMipMapLinearFilter;
          this._nebulaTextures.push(tex);

          const makeLayer = ({ texture, scale, opacity, z, rot, blend = THREE.AdditiveBlending, tint = 0xffffff }) => {
            // Oversized planes prevent the “center box” artifact (seeing the plane edges in view),
            // especially during boosted FOV. Keep it huge; it's only a few quads.
            const baseW = 18000;
            const baseH = 11000;
            const g = new THREE.PlaneGeometry(baseW * scale, baseH * scale, 1, 1);
            const m = new THREE.MeshBasicMaterial({
              map: texture,
              color: tint,
              transparent: true,
              opacity,
              blending: blend,
              depthWrite: false,
              fog: false,
            });
            const mesh = new THREE.Mesh(g, m);
            mesh.position.set(0, 0, z);
            mesh.rotation.z = rot;
            mesh.renderOrder = -10;
            mesh.frustumCulled = false;
            this.scene.add(mesh);
            this.nebulaLayers.push(mesh);
          };

          // Base authored nebula (keep vivid, but a touch darker so ships projectiles read).
makeLayer({ texture: tex, scale: 1.05, opacity: 0.18, z: -3800, rot: Math.random() * Math.PI * 2, tint: 0xbad6ff });
      makeLayer({ texture: tex, scale: 0.8, opacity: 0.10, z: -3200, rot: Math.random() * Math.PI * 2, tint: 0xffb3ff });

      // Procedural layers add depth and motion even with a single source image.
      const p1 = makeProceduralNebulaTexture({ inner: "#202a5e", mid: "#0a1736", outer: "#02040c" });
      const p2 = makeProceduralNebulaTexture({ inner: "#4b1d5e", mid: "#16102a", outer: "#02040c" });
      if (p1) {
        this._nebulaTextures.push(p1);
        makeLayer({ texture: p1, scale: 1.2, opacity: 0.14, z: -4200, rot: Math.random() * Math.PI * 2, tint: 0x66aaff });
      }
      if (p2) {
        this._nebulaTextures.push(p2);
        makeLayer({ texture: p2, scale: 0.95, opacity: 0.10, z: -3600, rot: Math.random() * Math.PI * 2, tint: 0xff66ff });
      }
        },
        undefined,
        () => {
          // Try fallback resolution.
          tryLoad(i + 1);
        }
      );
    };

    tryLoad(0);
  }
  
  createSkybox() {
    if (isTestEnvironment()) return;
    if (typeof document === "undefined" || typeof Image === "undefined") return;

    const skyboxGeometry = new THREE.SphereGeometry(4500, 32, 32);
    const loader = new THREE.TextureLoader();

    const applySky = (texture) => {
      const skyboxMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0.78,
        depthWrite: false,
        fog: false,
        toneMapped: false,
      });
      this.skybox = new THREE.Mesh(skyboxGeometry, skyboxMaterial);
      this.skybox.renderOrder = -100;
      this.scene.add(this.skybox);
    };

    const fallbackGradient = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;
      const context = canvas.getContext('2d');
      if (!context) return;

      const gradient = context.createRadialGradient(256, 256, 0, 256, 256, 256);
      gradient.addColorStop(0, '#1a1a3e');
      gradient.addColorStop(0.5, '#0a0a1a');
      gradient.addColorStop(1, '#000000');

      context.fillStyle = gradient;
      context.fillRect(0, 0, 512, 512);

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestMipMapNearestFilter;
      applySky(texture);
    };

    loader.load(
      '/ui/backgrounds/1024/nebula_n64_corridor.png',
      (tex) => {
        if (!tex) return fallbackGradient();
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.magFilter = THREE.NearestFilter;
        tex.minFilter = THREE.NearestMipMapNearestFilter;
        applySky(tex);
      },
      undefined,
      () => fallbackGradient()
    );
  }

  startBoardroomGateSetPiece(options = {}) {
    if (!this.scene) return null;

    const duration = Math.max(3, Number(options.duration) || 12);
    const label = String(options.label || "HOSTILE BOARDROOM GATE");
    const group = new THREE.Group();
    group.name = "boardroom-gate-setpiece";
    group.position.set(0, 0, -1150);
    group.userData = {
      kind: "boardroomGate",
      age: 0,
      duration,
      label,
    };

    const cyan = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const amber = new THREE.MeshBasicMaterial({
      color: 0xff7a00,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });

    for (let i = 0; i < 9; i++) {
      const radius = 160 + i * 28;
      const tube = i % 3 === 0 ? 3.5 : 2.0;
      const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, tube, 8, 80), i % 2 ? amber.clone() : cyan.clone());
      ring.position.z = -i * 125;
      ring.rotation.x = Math.PI / 2;
      ring.rotation.z = i * 0.27;
      ring.userData.spin = (i % 2 ? -1 : 1) * (0.12 + i * 0.025);
      group.add(ring);
    }

    const lineMat = new THREE.LineBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.34,
      blending: THREE.AdditiveBlending,
      fog: false,
    });
    for (let lane = -3; lane <= 3; lane++) {
      const points = [
        new THREE.Vector3(lane * 58, -105, 120),
        new THREE.Vector3(lane * 38, -55, -1120),
      ];
      const geom = new THREE.BufferGeometry().setFromPoints(points);
      group.add(new THREE.Line(geom, lineMat.clone()));
    }

    const shardGeom = new THREE.BoxGeometry(18, 4, 46);
    for (let i = 0; i < 36; i++) {
      const shard = new THREE.Mesh(shardGeom, i % 2 ? amber.clone() : cyan.clone());
      const angle = (i / 36) * Math.PI * 2;
      const radius = 220 + (i % 6) * 34;
      shard.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius * 0.62, -80 - i * 32);
      shard.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, angle);
      shard.userData.spin = 0.35 + (i % 5) * 0.07;
      group.add(shard);
    }

    this.scene.add(group);
    this.setPieces.push(group);
    return group;
  }

  updateSetPieces(deltaTime = 0.016, playerOrPosition = null) {
    if (!this.scene || this.setPieces.length === 0) return;
    const dt = Math.max(0, Number(deltaTime) || 0);
    const playerPosition =
      playerOrPosition?.object3D?.position && playerOrPosition.object3D.position.isVector3
        ? playerOrPosition.object3D.position
        : playerOrPosition?.isVector3
          ? playerOrPosition
          : null;

    for (let i = this.setPieces.length - 1; i >= 0; i--) {
      const group = this.setPieces[i];
      const data = group.userData || {};
      data.age = Math.max(0, Number(data.age) || 0) + dt;
      group.userData = data;

      const life = Math.min(1, data.age / Math.max(0.001, Number(data.duration) || 1));
      group.position.z += (180 + life * 120) * dt;
      group.position.x = (playerPosition?.x || 0) * 0.08;
      group.position.y = (playerPosition?.y || 0) * 0.05;

      for (const child of group.children) {
        child.rotation.z += (Number(child.userData?.spin) || 0.1) * dt;
        const mat = child.material;
        if (mat && typeof mat === "object" && "opacity" in mat) {
          const fadeIn = Math.min(1, data.age / 1.2);
          const fadeOut = Math.max(0, 1 - Math.max(0, life - 0.78) / 0.22);
          mat.opacity = (child.type === "Line" ? 0.34 : 0.28) * fadeIn * fadeOut;
        }
      }

      if (data.age >= data.duration || group.position.z > 260) {
        this.scene.remove(group);
        this.disposeObjectTree(group);
        this.setPieces.splice(i, 1);
      }
    }
  }
  
  updateCamera(playerOrPosition, deltaTime = 0.016) {
    if (!playerOrPosition) return;

    const playerPosition =
      playerOrPosition?.object3D?.position && playerOrPosition.object3D.position.isVector3
        ? playerOrPosition.object3D.position
        : playerOrPosition;
    if (!playerPosition?.isVector3) return;

    const isBoosting = Boolean(playerOrPosition?.isBoosting);
    const isBraking = Boolean(playerOrPosition?.isBraking);
    const v = playerOrPosition?.velocity;
    const vx = Number(v?.x) || 0;
    const vy = Number(v?.y) || 0;

    // Star Fox-like camera offsets:
    // - camera eye follows ship loosely
    // - stick movement biases eye/at ("camera lead")
    // - mild roll follows ship roll for cockpit-like feel without disorientation
    const leadX = Number(playerOrPosition?._cameraLead?.x) || 0;
    const leadY = Number(playerOrPosition?._cameraLead?.y) || 0;
    const shipRoll = Number(playerOrPosition?.object3D?.rotation?.z) || 0;

    const followZBase = 138;
    const followZ = followZBase + (isBoosting ? 52 : 0) - (isBraking ? 10 : 0);

    const eyeX = playerPosition.x * 0.24 - leadX * 24 - vx * 0.03;
    const eyeY = 24 + playerPosition.y * 0.12 - leadY * 12 - vy * 0.02 + (isBoosting ? 5 : 0);
    const desired = new THREE.Vector3(
      THREE.MathUtils.clamp(eyeX, -95, 95),
      THREE.MathUtils.clamp(eyeY, -32, 82),
      playerPosition.z + followZ
    );

    const t = 1 - Math.exp(-7.2 * Math.max(0.001, deltaTime));
    this.camera.position.lerp(desired, t);

    const baseFov = 62;
    const targetFov = isBoosting ? 72 : (isBraking ? 56 : baseFov);
    this.camera.fov = THREE.MathUtils.lerp(
      this.camera.fov || baseFov,
      targetFov,
      1 - Math.exp(-6 * Math.max(0.001, deltaTime))
    );
    this.camera.updateProjectionMatrix();

    const at = new THREE.Vector3(
      playerPosition.x * 0.16 - leadX * 7.5,
      16 + playerPosition.y * 0.11 - leadY * 3.5,
      playerPosition.z - 360
    );
    this.camera.lookAt(at);

    const camBank = THREE.MathUtils.clamp(-shipRoll * 0.22, -0.22, 0.22);
    this.camera.rotation.z = THREE.MathUtils.lerp(
      this.camera.rotation.z || 0,
      camBank,
      1 - Math.exp(-5.8 * Math.max(0.001, deltaTime))
    );

    // Environment motion: subtle star drift + dust "speed" + nebula parallax.
    if (this.stars) this.stars.rotation.z += deltaTime * 0.01;
    if (this.starsFar) this.starsFar.rotation.z -= deltaTime * 0.004;

    const dust = this.dust;
    const dustVel = this._dustVel;
    if (dust && dust.geometry?.attributes?.position && dustVel) {
      const arr = dust.geometry.attributes.position.array;
      const speedMul = isBoosting ? 3.2 : (isBraking ? 0.65 : 1.0);
      for (let i = 0; i < dustVel.length; i++) {
        const i3 = i * 3;
        arr[i3 + 2] += dustVel[i] * speedMul * deltaTime;
        if (arr[i3 + 2] > this.camera.position.z + 120) {
          arr[i3] = (Math.random() - 0.5) * 900;
          arr[i3 + 1] = (Math.random() - 0.5) * 520;
          arr[i3 + 2] = -3000 - Math.random() * 1200;
        }
      }
      dust.geometry.attributes.position.needsUpdate = true;
      if (dust.material && typeof dust.material === "object") {
        dust.material.opacity = isBoosting ? 0.4 : 0.22;
      }
    }

    if (this.nebulaLayers.length > 0) {
      const px = playerPosition.x;
      const py = playerPosition.y;
      for (let i = 0; i < this.nebulaLayers.length; i++) {
        const layer = this.nebulaLayers[i];
        if (!layer) continue;
        // Follow camera lightly (parallax) and keep it facing the camera.
        layer.position.x = this.camera.position.x * 0.25 + px * 0.18 * (i + 1);
        layer.position.y = this.camera.position.y * 0.18 + py * 0.12 * (i + 1);
        layer.quaternion.copy(this.camera.quaternion);

        const mat = layer.material;
        // Keep nebula texture coordinates static. Camera/layer parallax above
        // supplies motion without the boost-time wrap seam artifact.
        if (mat && typeof mat === "object" && "opacity" in mat) {
          const base = i === 0 ? 0.18 : 0.12;
          mat.opacity = base + (isBoosting ? 0.02 : 0) - (isBraking ? 0.02 : 0);
        }
      }
    }

    this.updateSetPieces(deltaTime, playerOrPosition);
  }
  
  onResize(width, height, fov) {
    this.camera.aspect = Math.max(0.1, width / height);
    if (typeof fov === 'number' && fov > 0) {
      this.camera.fov = fov;
    }
    this.camera.updateProjectionMatrix();
  }
  
  dispose() {
    if (this.stars) {
      this.stars.geometry.dispose();
      this.stars.material.dispose();
    }
    
    if (this.skybox) {
      this.skybox.geometry.dispose();
      this.skybox.material.dispose();
    }
    for (const group of this.setPieces) {
      this.scene?.remove?.(group);
      this.disposeObjectTree(group);
    }
    this.setPieces = [];
  }

  disposeObjectTree(root) {
    root?.traverse?.((obj) => {
      if (obj.geometry?.dispose) obj.geometry.dispose();
      const material = obj.material;
      if (Array.isArray(material)) {
        material.forEach((m) => m?.dispose?.());
      } else {
        material?.dispose?.();
      }
    });
  }
}
